import React, { useState } from "react";
import { AiOutlinePlus, AiOutlineMinus } from "react-icons/ai";
import redraftData from "../../data/redraft-2016.json";
import ShareButtons from "../../components/ShareButtons";

const ExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({
  href,
  children,
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="underline text-[#A8583A] hover:text-[#8A4630]"
  >
    {children}
  </a>
);

const CollapsibleBox: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="my-4">
      <button
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900 transition"
      >
        <span className="p-1 rounded-full bg-slate-200 shadow-sm hover:bg-slate-300 transition">
          {open ? <AiOutlineMinus /> : <AiOutlinePlus />}
        </span>
        {label}
        <span className="font-normal text-xs text-slate-700">
          (click to expand)
        </span>
      </button>
      {open && (
        <div className="mt-2 bg-slate-50 rounded-xl ring-1 ring-slate-200 shadow-sm p-4 animate-fade-in text-slate-600 text-sm space-y-2 text-justify">
          {children}
        </div>
      )}
    </div>
  );
};

const ordinal = (n: number): string => {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

const pickLabel = (pick: number | string | null | undefined): string => {
  if (pick === null || pick === undefined) return "";
  if (typeof pick === "number") return `${ordinal(pick)} pick`;
  if (pick === "UDFA") return "undrafted";
  return `${pick} pick`;
};

const RedraftTable: React.FC = () => {
  const [expandedRank, setExpandedRank] = useState<number | null>(null);

  const toggleRow = (rank: number) => {
    setExpandedRank((prev) => (prev === rank ? null : rank));
  };

  return (
    <div className="my-4">
      <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200">
        <table className="min-w-full text-sm text-left text-slate-700">
          <thead className="bg-slate-100 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Player</th>
              <th className="px-3 py-2">Score</th>
              <th className="px-3 py-2 text-right">Read More</th>
            </tr>
          </thead>
          <tbody>
            {redraftData.players.map((p) => (
              <React.Fragment key={p.rank}>
                <tr className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-900">
                    {p.rank}. {p.player}
                    {p.pick !== null && p.pick !== undefined && (
                      <span className="text-slate-500 font-normal">
                        {" "}
                        ({pickLabel(p.pick)})
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{p.score ?? "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => toggleRow(p.rank)}
                      aria-expanded={expandedRank === p.rank}
                      className="text-xs p-1 rounded-full bg-slate-200 hover:bg-slate-300 transition"
                    >
                      {expandedRank === p.rank ? (
                        <AiOutlineMinus />
                      ) : (
                        <AiOutlinePlus />
                      )}
                    </button>
                  </td>
                </tr>
                {expandedRank === p.rank && (
                  <tr className="bg-slate-50">
                    <td colSpan={3} className="px-3 py-3 text-slate-600 whitespace-pre-line text-justify">
                      {p.read_more}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs italic text-slate-500">{redraftData.caption}</p>
    </div>
  );
};

const composite2025 = [
  { player: "Nikola Jokić", score: 11.31, darkoRank: 1 },
  { player: "Victor Wembanyama", score: 10.57, darkoRank: 3 },
  { player: "Shai Gilgeous-Alexander", score: 10.11, darkoRank: 4 },
  { player: "Kawhi Leonard", score: 9.49, darkoRank: 2 },
  { player: "Luka Dončić", score: 8.62, darkoRank: 6 },
  { player: "Karl-Anthony Towns", score: 8.36, darkoRank: 12 },
  { player: "OG Anunoby", score: 8.25, darkoRank: 14 },
  { player: "Donovan Mitchell", score: 8.23, darkoRank: 8 },
  { player: "Jalen Brunson", score: 7.99, darkoRank: 16 },
  { player: "Cade Cunningham", score: 7.84, darkoRank: 11 },
  { player: "Anthony Edwards", score: 7.75, darkoRank: 10 },
  { player: "Chet Holmgren", score: 7.66, darkoRank: 9 },
  { player: "Kevin Durant", score: 7.34, darkoRank: 15 },
  { player: "Giannis Antetokounmpo", score: 7.13, darkoRank: 5 },
  { player: "Scottie Barnes", score: 7.10, darkoRank: 26 },
  { player: "Tyrese Maxey", score: 7.05, darkoRank: 28 },
  { player: "Jamal Murray", score: 6.92, darkoRank: 20 },
  { player: "James Harden", score: 6.92, darkoRank: 25 },
  { player: "Bam Adebayo", score: 6.49, darkoRank: 22 },
  { player: "Evan Mobley", score: 6.48, darkoRank: 33 },
  { player: "Rudy Gobert", score: 6.17, darkoRank: 32 },
  { player: "Jarrett Allen", score: 6.08, darkoRank: 17 },
  { player: "Jaylen Brown", score: 6.064, darkoRank: 35 },
];

const JaylenBrown2016Redraft: React.FC = () => {

  const shareTitle =
    "Re-evaluating Jaylen Brown and his 2016 Draft peers | Hoops Boarding Gate";
  const shareDescription =
    "A 10-year, DRAPE-based re-draft of the 2016 NBA Draft class, breaking down where Jaylen Brown and his peers really stack up.";
  const shareImage = "https://www.hoops-bg.net/assets/2016-redraft.png";

  return (
    <div className="min-h-screen bg-[#F6F4F0] p-6" dir="ltr">
      <div className="relative mx-auto max-w-3xl text-slate-700">
        <ShareButtons
          isHE={false}
          photoURL={shareImage}
          stickyTop
          title={shareTitle}
          description={shareDescription}
          accentColor="#A8583A"
        />

        <h1 className="text-2xl font-bold mb-6" style={{ color: "#A8583A" }}>
          Re-evaluating Jaylen Brown and his 2016 Draft peers
        </h1>

        <img
          src="/assets/2016-redraft.png"
          alt="2016 NBA Draft re-draft graphic"
          className="mx-auto mb-4 block w-64 rounded-xl shadow-md ring-1 ring-slate-200 sm:float-right sm:mx-0 sm:ml-6 sm:w-72 md:w-80"
        />

        <p className="mb-4 leading-relaxed text-justify">
          Following a week during which the Jaylen Brown trade has reignited{" "}
          <ExternalLink href="https://basketball.realgm.com/wiretap/286530/Jaylen-Brown-Trade-Ignites-NBA-Wide-Analytics-Debate">
            a leaguewide argument over analytics-driven roster building
          </ExternalLink>
          , we present to you DRAPE — a formula that I developed using a
          Claude Desktop agent for evaluating past NBA Draft classes and
          creating objective re-draft rankings. The D in the formula's
          acronym stands for DARKO, or DPM (Daily Plus-Minus). DARKO-DPM was
          ranked as the #1 catch-all, all-in-one advanced statistic in a poll
          that{" "}
          <ExternalLink href="https://www.hoopshype.com/story/sports/nba/2021/09/17/advanced-stats-nba-real-plus-minus-rapm-win-shares-analytics/75615174007/">
            HoopsHype
          </ExternalLink>{" "}
          conducted early in this decade among nearly 30 participants,
          "including various media members as well as individuals who have a
          combined experience with more than half of the teams in the NBA".
        </p>

        <CollapsibleBox label="What are DARKO and DPM">
          <p>
            DARKO is a Bayesian projection model, and DPM — Daily Plus-Minus —
            is the all-in-one rating it produces: an estimate of a player's
            contribution to point differential per 100 possessions. Because
            it isn't built from box-score totals alone, DPM captures value
            that a simple stat line misses, including the "invisible"
            contributions of perimeter stoppers and rim protectors.
          </p>
        </CollapsibleBox>

        <p className="mb-4 leading-relaxed text-justify">
          Relying solely on DARKO for redraft rankings would lead to some
          anomalies, such as Brandon Clarke (RIP) as the 3rd best player on
          the 2019 draft, or Isaiah Joe at #8 on the 2020 draft. I wanted to
          avoid such anomalies and have a formula that accounts for volume,
          availability, and playoff production. Enter DRAPE — it "drapes"
          context over the raw DPM rate. It stands for DARKO Rate,
          Availability, Playoffs production, and Endurance (minutes load).
        </p>

        <CollapsibleBox label="More on the added factors">
          <p>
            <span className="font-semibold text-slate-900">
              The Volume Factor (Minutes &amp; Load):
            </span>{" "}
            We reward players who carry heavy workloads. Using a combination
            of Recent MPG and Total Career Minutes (Load), we distinguish
            between "per-minute darlings" who thrive in limited roles and
            true "workhorses" like Domantas Sabonis or Jaden McDaniels. Both the Minutes-per-game (M) and the Load weights were given the value of 2.
          </p>
          <p>
            <span className="font-semibold text-slate-900">
              The Postseason Stakes:
            </span>{" "}
            To account for "contender value," we include an additive bonus
            for career playoff production. This prevents the model from
            ignoring the "title pedigree" of players who may have slightly
            lower regular-season impact but historically elevate their game
            in the spring. The Playoff-stakes weight has been given the value of 0.8
          </p>
          <p>
            <span className="font-semibold text-slate-900">
              The Asymmetric Availability Factor:
            </span>{" "}
            We implemented a "signed" availability lever. Using a baseline of
            62 games per season, the formula:
          </p>
          <ul className="list-disc list-outside pl-5 space-y-1 text-justify">
            <li>Rewards "Iron Men" who consistently exceed the threshold.</li>
            <li>Penalizes players who fall below it.</li>
            <li>
              Grace Period: To remain fair, the model excludes seasons where a
              player was completely inactive (e.g., Tyrese Haliburton's
              2025-26), ensuring a freak injury doesn't permanently, or even temporarily, ruin a
              player's historical ranking. The Availability weight has been given a value of 0.2
            </li>
          </ul>
        </CollapsibleBox>

        <h2 className="text-lg font-semibold text-slate-900 mt-6 mb-2">
          Filtering Incoming Data
        </h2>
        <p className="mb-2 leading-relaxed text-justify">
          A major breakthrough in the model was establishing a standardized
          Removal Philosophy. We found that raw career averages are often
          "poisoned" by seasons that don't represent a player's true value.
          We apply a strict three-rule filter to exclude specific seasons
          before calculating a player's score:
        </p>
        <ul className="list-disc list-outside pl-5 space-y-1 mb-4 text-justify">
          <li>
            <span className="font-semibold text-slate-900">Developmental:</span>{" "}
            We remove inefficient rookie seasons (defined as below-league-average
            shooting and net-negative value) or early years where a player
            was buried on the depth chart.
          </li>
          <li>
            <span className="font-semibold text-slate-900">
              Change-of-Context:
            </span>{" "}
            Seasons where a player was used incorrectly or stuck behind an
            established star before a breakout (e.g., Isaiah Joe in Philly or
            Nesmith in Boston, and perhaps most notably, Alexander-Walker's
            first three seasons with the Pelicans) are excluded.
          </li>
          <li>
            <span className="font-semibold text-slate-900">Injury:</span> Lost
            seasons due to major injury are removed to prevent "durability
            double-dipping" in the rate-based portion of the model.
          </li>
        </ul>

        <h2 className="text-lg font-semibold text-slate-900 mt-6 mb-2">
          Recency Weighting
        </h2>
        <p className="mb-2 leading-relaxed text-justify">
          The model is not a career retrospective; it is a redraft based on
          current information. We utilize a graduated year-weighting system:
        </p>
        <ul className="list-disc list-outside pl-5 space-y-1 mb-2 text-justify">
          <li>Years 1–2 are baseline (1.0).</li>
          <li>Years 3–4 (extension seasons) receive a moderate lift (1.7).</li>
          <li>Years 5+ (prime years) are weighted most heavily (2.0–2.5).</li>
        </ul>
        <p className="mb-4 leading-relaxed text-justify">
          This ensures that a player's current trajectory is the primary
          driver of their rank, allowing late bloomers (like Nickeil
          Alexander-Walker and Deni Avdija) to rise above players who peaked
          early and plateaued (Tyler Herro and LaMelo Ball).
        </p>

        <p className="mb-6 leading-relaxed text-justify">
          Since we fine-tuned DRAPE using the 2016 to 2018 drafts, with some
          input from the more recent 2019 and 2020 classes, we'll present the
          formula's findings on the 2016 draft, whose players just completed
          their 10th pro season. Its #1 player — spoiler alert! — isn't the
          analytics darling you might expect, as recent developments have
          shown.
        </p>

        <h2 className="text-xl font-bold text-slate-900 mt-8 mb-3">
          The 2016 NBA Draft DRAPE-based Re-draft 10-Years Rankings
        </h2>
        <RedraftTable />

        <h3 className="text-base font-semibold text-slate-900 mt-6 mb-2">
          Some Observations and Insights
        </h3>
        {redraftData.observations.map((paragraph, idx) => (
          <p key={idx} className="mb-4 leading-relaxed text-justify text-slate-600">
            {paragraph}
          </p>
        ))}

        <p className="mb-6 leading-relaxed text-justify">
          So we see that DRAPE actually appreciates Jaylen Brown, but one
          might argue that the 2016 draft class wasn't much to begin with.
          How does DRAPE rank Brown in comparison to players who were
          drafted before him and after?
        </p>

        <h2 className="text-lg font-semibold text-slate-900 mt-6 mb-2">
          Brown vs the top players of the 2017 &amp; 2018 drafts
        </h2>
        <p className="mb-4 leading-relaxed text-justify">
          I pooled Brown with the top 2017 and 2018 players and normalized
          all volume terms to the combined pool max, so the scores are
          actually cross-comparable (unlike the within-class boards).
        </p>
        <p className="mb-6 leading-relaxed text-justify">
          Tatum, Luka, Mitchell and SGA all clear Brown by a full tier —
          their DARKO rates (3.1–4.2) roughly double his (1.9). And removing
          his rookie 2016-17 does help: it lifts his rate to 2.11 and moves
          him from #7 to #5, nudging past Brunson — but it doesn't touch the
          top group. What's holding Brown up is volume plus his 4.0 career
          playoff VORP (the 2024 title run), not rate.
        </p>

        <h2 className="text-lg font-semibold text-slate-900 mt-6 mb-2">
          Single-season 2025/26 composite (league-wide, 39-player pool)
        </h2>
        <p className="mb-2 leading-relaxed text-justify">
          Applying the composite (rate + M·mpgN + L·loadN + P·povN +
          A·availTerm, M2/L2/P1.5) to the pure-DARKO leaderboard moves Brown
          from #35 → #23 (A=0.2), or #25 (A=0.1).
        </p>
        <p className="mb-4 leading-relaxed text-justify">
          He leapfrogs precisely the usual suspects — the high-rate/low-volume
          guys the composite is designed to push back in the rankings. As we can see, Brown
          moves from 35 on pure-DARKO rating to #23 at 6.064 with the DRAPE
          composite formula. Ahead of him, with composite score (and where
          they sat on the pure-DARKO leaderboard):
        </p>

        <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200 mb-8">
          <table className="min-w-full text-sm text-left text-slate-700">
            <thead className="bg-slate-100 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Player</th>
                <th className="px-3 py-2">Composite score</th>
                <th className="px-3 py-2">Pure-DARKO rank</th>
              </tr>
            </thead>
            <tbody>
              {composite2025.map((row, idx) => (
                <tr
                  key={row.player}
                  className={`border-t border-slate-100 ${
                    row.player === "Jaylen Brown"
                      ? "bg-[#A8583A]/10 font-semibold text-slate-900"
                      : "hover:bg-slate-50"
                  }`}
                >
                  <td className="px-3 py-2">
                    {idx + 1}. {row.player}
                  </td>
                  <td className="px-3 py-2">{row.score}</td>
                  <td className="px-3 py-2">#{row.darkoRank}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mb-6 leading-relaxed text-justify">
          Even if we argue that Brown, on his peak 2025/26 season, should be
          ranked ahead of players like Allen, Gobert, Mobley, Bam, Harden,
          even Holmgren and Anunoby, this still puts him at #16, before
          accounting for the returns of Tatum and Haliburton next season. It
          appears that Brown can be considered a top-20 or a top-30 player,
          depending on your subjective evaluation.
        </p>
      </div>
    </div>
  );
};

export default JaylenBrown2016Redraft;
